import React, { useState } from "react";
import { api } from "../lib/api";
import { OptionButton } from "./OptionButton";

const STAGE_META = {
  idiom:   { icon: "🎵", label: "Deyimi Yakala" },
  meaning: { icon: "💡", label: "Anlamını Yakala" },
  context: { icon: "🧠", label: "Bağlamı Yakala" },
};

export function PersonalResult({ code, playerId, name, summary }) {
  const [review, setReview] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [loadingReview, setLoadingReview] = useState(false);

  const loadReview = async () => {
    setLoadingReview(true);
    try {
      const r = await api.get(`/games/${code}/players/${playerId}/review`);
      setReview(r.data); setReviewOpen(true);
    } catch {}
    finally { setLoadingReview(false); }
  };

  if (!summary) {
    return (
      <div className="min-h-screen dark bg-[#070B14] text-white flex items-center justify-center">
        <div className="text-zinc-400 text-sm">Sonuç kartın hazırlanıyor…</div>
      </div>
    );
  }

  if (reviewOpen && review) return <ReviewMode data={review} onClose={() => setReviewOpen(false)} />;

  const rankMedal = summary.rank === 1 ? "🥇" : summary.rank === 2 ? "🥈" : summary.rank === 3 ? "🥉" : "🏅";
  const learned = summary.deyim_karnem.filter((d) => d.status === "learned");
  const review_items = summary.deyim_karnem.filter((d) => d.status === "review");
  const totalIdioms = summary.deyim_karnem.length || 1;
  const learnedPct = Math.round((learned.length / totalIdioms) * 100);

  return (
    <div className="min-h-screen text-white"
         style={{ background: "linear-gradient(180deg, #070B14 0%, #0B1020 100%)" }}>
      <div className="max-w-md mx-auto px-5 py-8">
        <div className="text-center">
          <div className="text-xs font-heading tracking-widest text-cyan-300">🎧 OYUN SONUCUN</div>
          <div className="mt-3 flex items-center justify-center gap-2 text-3xl">{rankMedal}</div>
          <div className="mt-1 font-heading text-3xl font-bold" data-testid="my-final-rank">{summary.rank}. OLDUN!</div>
          <div className="mt-2 font-heading text-5xl font-black tabular-nums bg-gradient-to-br from-cyan-300 to-violet-400 bg-clip-text text-transparent" data-testid="my-final-score">
            {(summary.score || 0).toLocaleString("tr-TR")}
          </div>
          <div className="text-xs text-zinc-500 tracking-widest mt-1">TOPLAM PUAN</div>

          <div className="mt-6 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <div className="text-[10px] text-zinc-500">DOĞRU</div>
              <div className="font-heading text-lg" data-testid="my-correct-total">{summary.correct}/{summary.total_questions}</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <div className="text-[10px] text-zinc-500">BAŞARI</div>
              <div className="font-heading text-lg">%{summary.accuracy}</div>
            </div>
            <div className="rounded-xl border border-amber-400/20 bg-amber-500/5 p-3">
              <div className="text-[10px] text-amber-300/80">3/3 BONUS</div>
              <div className="font-heading text-lg text-amber-200">🏆 {summary.perfect_idioms}</div>
            </div>
          </div>

          {/* Deyim ustalığı progress ring */}
          <div className="mt-8 flex items-center justify-center gap-5">
            <ProgressRing percent={learnedPct} />
            <div className="text-left">
              <div className="text-[10px] font-heading tracking-widest text-cyan-300">DEYİM USTALIĞI</div>
              <div className="mt-1 font-heading text-2xl">{learned.length} / {totalIdioms} <span className="text-zinc-400 text-sm">öğrenildi</span></div>
              {review_items.length > 0 && (
                <div className="text-xs text-zinc-400 mt-0.5">{review_items.length} deyim biraz daha çalışılmalı</div>
              )}
            </div>
          </div>
        </div>

        <section className="mt-10">
          <div className="text-xs font-heading tracking-widest text-cyan-300">ÖĞRENME PERFORMANSIN</div>
          <div className="mt-3 space-y-2">
            {["idiom", "meaning", "context"].map((s) => {
              const st = summary.stage_stats[s]; const meta = STAGE_META[s];
              return (
                <div key={s} className="rounded-xl border border-white/10 bg-zinc-950/70 p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-heading text-sm">{meta.icon} {meta.label}</div>
                    <div className="font-heading text-sm tabular-nums">{st.correct}/{st.total} — %{st.rate}</div>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-400 to-violet-400" style={{ width: `${Math.min(100, st.rate)}%` }}/>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {summary.feedback.length > 0 && (
          <section className="mt-8">
            <div className="text-xs font-heading tracking-widest text-cyan-300">GERİ BİLDİRİM</div>
            <div className="mt-3 space-y-2">
              {summary.feedback.map((f, i) => (
                <div key={i} className="rounded-xl border border-cyan-400/20 bg-cyan-500/5 px-4 py-3 text-sm text-cyan-100 font-literary italic">"{f}"</div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10">
          <div className="text-xs font-heading tracking-widest text-cyan-300">📖 DEYİM KARNEM</div>
          <div className="mt-3 space-y-1.5">
            {learned.map((d) => (
              <div key={d.idiom_id} className="flex items-center gap-2 rounded-lg border border-emerald-400/20 bg-emerald-500/5 px-3 py-2 text-sm">
                <span className="text-emerald-300">✓</span>
                <span className="font-literary italic">"{d.idiom}"</span>
                <span className="ml-auto text-emerald-300 text-[10px] font-heading">ÖĞRENDİM</span>
              </div>
            ))}
            {review_items.map((d) => (
              <div key={d.idiom_id} className="flex items-center gap-2 rounded-lg border border-yellow-400/20 bg-yellow-500/5 px-3 py-2 text-sm">
                <span className="text-yellow-300">△</span>
                <span className="font-literary italic">"{d.idiom}"</span>
                <span className="ml-auto text-yellow-300 text-[10px] font-heading">TEKRAR</span>
              </div>
            ))}
            {summary.deyim_karnem.length === 0 && <div className="text-zinc-500 text-sm">Bu oyunda hiç deyim işlenmedi.</div>}
          </div>
        </section>

        {review_items.length > 0 && (
          <button onClick={loadReview} disabled={loadingReview} data-testid="review-btn"
                  className="mt-8 w-full rounded-full bg-gradient-to-r from-amber-300 to-cyan-300 text-black font-heading font-bold py-4 hover:opacity-90 disabled:opacity-40">
            🔁 TEKRAR ZAMANI
          </button>
        )}

        <div className="mt-6 text-center text-xs text-zinc-600">
          Bu sonuç sadece sana özeldir. Sınıf tahtasında gösterilmez.
        </div>
      </div>
    </div>
  );
}

function ReviewMode({ data, onClose }) {
  const [i, setI] = useState(0);
  const [qi, setQi] = useState(0);
  const [selected, setSelected] = useState(null);

  if (data.count === 0) {
    return (
      <div className="min-h-screen dark bg-[#070B14] text-white flex items-center justify-center p-6 text-center">
        <div>
          <div className="text-4xl">🎉</div>
          <p className="mt-4 font-heading text-xl">Çalışılacak hatan yok!</p>
          <button onClick={onClose} className="mt-6 rounded-full border border-white/20 px-5 py-2">Kapat</button>
        </div>
      </div>
    );
  }

  const item = data.items[i];
  const q = item.questions[qi];
  const isCorrect = selected && selected === q.correct;
  const isAnswered = selected !== null;

  const next = () => {
    if (qi + 1 < item.questions.length) { setQi(qi + 1); setSelected(null); }
    else if (i + 1 < data.items.length) { setI(i + 1); setQi(0); setSelected(null); }
    else { onClose(); }
  };

  return (
    <div className="min-h-screen text-white"
         style={{ background: "linear-gradient(180deg, #070B14 0%, #0B1020 100%)" }}>
      <div className="max-w-md mx-auto px-5 py-6">
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <div>🔁 <span className="text-amber-300 font-heading">TEKRAR ZAMANI</span></div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="review-close-btn">Kapat ×</button>
        </div>
        <p className="mt-2 text-xs text-zinc-400 italic">Biraz daha çalışman gereken deyimleri yeniden deneyelim.</p>
        <div className="mt-4 text-[10px] tracking-widest text-zinc-500 font-heading">DEYİM {i+1}/{data.items.length}</div>
        <div className="mt-2 rounded-2xl border border-white/10 bg-zinc-950/70 p-4">
          <div className="font-literary italic text-xl">"{item.idiom}"</div>
          <div className="text-zinc-500 text-xs mt-1">{item.artist} — {item.song}</div>
          <div className="text-zinc-300 text-sm mt-2">{item.meaning}</div>
        </div>
        <div className="mt-6">
          <div className="text-[10px] font-heading tracking-widest text-cyan-300">
            {STAGE_META[q.stage].icon} {STAGE_META[q.stage].label.toUpperCase()}
          </div>
          <div className="mt-3 rounded-2xl border border-white/10 bg-zinc-950/70 p-4">
            <p className="font-literary text-lg leading-snug">{q.text}</p>
          </div>
          <div className="mt-4 space-y-3">
            {q.options.map((opt, oi) => {
              const L = "ABCDE"[oi];
              return (
                <OptionButton key={L} letter={L}
                  onClick={() => !isAnswered && setSelected(L)}
                  disabled={isAnswered}
                  selected={selected === L}
                  correct={isAnswered && q.correct === L}
                  showResult={isAnswered}>
                  {opt}
                </OptionButton>
              );
            })}
          </div>
          {isAnswered && (
            <div className={`mt-4 text-center font-heading text-sm ${isCorrect ? "text-emerald-300" : "text-yellow-300"}`}>
              {isCorrect ? "✓ Doğru!" : `Doğru cevap: ${q.correct}`}
            </div>
          )}
          {isAnswered && (
            <button onClick={next} className="mt-6 w-full rounded-full bg-white text-black font-heading font-bold py-3">
              Devam →
            </button>
          )}
        </div>
        <div className="mt-6 text-center text-xs text-zinc-600">
          Bu mod puanı etkilemez. Yalnızca bireysel tekrar içindir.
        </div>
      </div>
    </div>
  );
}


function ProgressRing({ percent = 0, size = 84, stroke = 8 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, percent)) / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} className="stroke-white/10" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" strokeLinecap="round"
          className="stroke-cyan-300 transition-[stroke-dasharray] duration-700"
          strokeDasharray={`${dash} ${c - dash}`}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-heading text-xl">%{percent}</div>
    </div>
  );
}
